import { forEach, get, isEmpty, isNil } from 'lodash';
import {
  EXPAND_RISK,
  PARTIAL_JUGEMENT,
  RELIQUAT_APPEL,
  RELIQUAT_JUGEMENT,
  TOTAL_JUGEMENT,
} from 'containers/CourtManagerLegalProceedings/Provision/Regulation/Recap/constants';
import { RISK_NAME } from 'containers/CourtManagerLegalProceedings/Provision/constants';
import { RcCorporeal } from 'containers/CourtManagerLegalProceedings/Provision/Regulation/typings';
import { REGULATIONS } from 'containers/CourtManagerLegalProceedings/Provision/Regulation/data-structure';
import { SagisConfig } from 'containers/AppContainer/typings';
import {
  DOCUMENT_NATURE,
  RECIPIENT,
} from 'containers/CourtManagerLegalProceedings/Provision/Regulation/constants';
import {
  getFeesProvAmount,
  getVictimDetails,
  isStopEntry,
} from 'containers/CourtManagerLegalProceedings/Provision/Regulation/utils';
import { FEATURES } from 'features/features';
import * as featureService from 'features/fflip-helpers';
import { INJURY_POST } from 'containers/PreOpeningPage/constants';
import moment from 'moment';
import { VictimType } from 'typings/globalTypes';

export const getExpanded = (risk: any, riskRecap: any) => {
  const expanded = {
    [risk.regulationId]: {
      ...(risk.riskName === RISK_NAME.CORPOREAL
        ? {
            [EXPAND_RISK.SHOW]: !isEmpty(risk),
            [EXPAND_RISK.MINOR]: false,
            [EXPAND_RISK.ADULT]: false,
            [EXPAND_RISK.INFO]: true,
          }
        : {
            [EXPAND_RISK.SHOW]: isEmpty(
              riskRecap.find(
                (riskrecap: any) => riskrecap.riskName === RISK_NAME.CORPOREAL,
              ) || {},
            ),
            [EXPAND_RISK.DEFAULT]: false,
            [EXPAND_RISK.INFO]: true,
          }),
    },
  };
  return { ...(!isEmpty(risk) ? expanded : undefined) };
};
export const isSubstituteNatureRemainder = (
  documentNature: any[],
  rcCorporeal: RcCorporeal,
) => {
  const remainderSubstitute =
    documentNature.find(
      (item: SagisConfig) =>
        item.ItemDescription === DOCUMENT_NATURE.REMAINDER.title,
    ) || {};

  const isRemainder =
    get(rcCorporeal.regulationInfo, REGULATIONS.REGULATION_NATURE) ===
    get(remainderSubstitute, 'ItemKey');
  const isAppealedRemainder =
    documentNature.find(
      (item: SagisConfig) =>
        item.ItemDescription === DOCUMENT_NATURE.TOTAL_APPEAL.title,
    ) || {};
  return {
    isRemainder,
    isAppealedRemainder: get(isAppealedRemainder, 'ItemKey', ''),
  };
};

export const enableRemainingAmountField = (natureCode: string) =>
  String(natureCode) === RELIQUAT_JUGEMENT ||
  String(natureCode) === RELIQUAT_APPEL;
export const isPartialExecution = (natureCode: string) =>
  natureCode === PARTIAL_JUGEMENT;
export const enableRemainingJudgement = (natureCode: string) =>
  natureCode === RELIQUAT_JUGEMENT;
export const enableExecutionProvision = (natureCode: string) =>
  natureCode === PARTIAL_JUGEMENT || natureCode === TOTAL_JUGEMENT;

export const getProvisionExecutionValue = (
  fees: any,
  victim: VictimType,
  rcCorporeal: RcCorporeal,
  corpoEncryptedDocumentsResult: any,
) => {
  const partialOrTotalNature = enableExecutionProvision(
    get(rcCorporeal, 'regulationInfo.natureCode'),
  );

  if (partialOrTotalNature) {
    return get(
      getFeesProvAmount(
        get(victim, 'victimKey', ''),
        -1,
        corpoEncryptedDocumentsResult,
        fees,
      ),
      'provisionalExecutionAmount',
      '',
    );
  }
  if (
    enableRemainingAmountField(get(rcCorporeal, 'regulationInfo.natureCode'))
  ) {
    return get(
      getFeesProvAmount(
        get(victim, 'victimKey', ''),
        -1,
        corpoEncryptedDocumentsResult,
        fees,
      ),
      'remainderAmount',
      '',
    );
  }
};

export const reverseText = (text: any) => {
  return text
    .split('')
    .reverse()
    .join('');
};
export const showSummarySheet = (
  showValidationButton: boolean,
  paymentMethod: SagisConfig[],
  rcCorporeal: RcCorporeal,
  showProvisionDialog: boolean,
) => {
  return (
    (featureService.hasFeature(FEATURES.REGULATION_V2)
      ? !showValidationButton && !showProvisionDialog
      : showValidationButton && !showProvisionDialog) &&
    !isStopEntry(rcCorporeal, paymentMethod)
  );
};
export const showCdraSheet = (
  rcCorporeal: RcCorporeal,
  sagisClaim: any,
  corpoEncryptedDocumentsResult: any,
) => {
  const hasAdultVictim = getVictimDetails(
    rcCorporeal,
    sagisClaim,
    corpoEncryptedDocumentsResult,
    [],
  ).some((item: any) => !item.minor);

  return (
    hasAdultVictim &&
    get(rcCorporeal.regulationInfo, REGULATIONS.BENEFICIARY_RECIPIENT) ===
      RECIPIENT.CDRA &&
    featureService.hasFeature(FEATURES.REGULATION_V2)
  );
};
export const showCnraSheet = (
  rcCorporeal: RcCorporeal,
  sagisClaim: any,
  corpoEncryptedDocumentsResult: any,
) => {
  const victimModel = getVictimDetails(
    rcCorporeal,
    sagisClaim,
    corpoEncryptedDocumentsResult,
    [],
  ).filter((item: any) => item.minor);

  return (
    !isEmpty(victimModel) && featureService.hasFeature(FEATURES.REGULATION_V2)
  );
};
const groupByVictimName = (data: any[]) => {
  return data.reduce((acc, curr) => {
    const victimName = curr.victimName;

    if (!acc[victimName]) {
      acc[victimName] = [];
    }
    acc[victimName].push(curr);

    return acc;
  }, {});
};
export const extractVictims = (victims: any, corpoVictims: any[]) => {
  const { adults, minors } = victims;

  const allVictims = [
    ...(adults?.encryptedDocumentsOfAdults ?? []),
    ...(minors?.encryptedDocumentsOfMinors ?? []),
  ];

  const blesseeMajorVictims: any[] = [];
  const blesseeMinorVictims: any[] = [];
  const deceaseVictims: any[] = [];

  forEach(corpoVictims, (victim: VictimType) => {
    const { victimKey, injuryPostKey } = victim;

    if (!isEmpty(adults?.encryptedDocumentsOfAdults)) {
      blesseeMajorVictims.push(
        ...adults.encryptedDocumentsOfAdults.filter(
          (v: any) =>
            v.victimKey.toString() === victimKey &&
            INJURY_POST.INJURED.includes(injuryPostKey),
        ),
      );
    }

    if (!isEmpty(minors?.encryptedDocumentsOfMinors)) {
      blesseeMinorVictims.push(
        ...minors.encryptedDocumentsOfMinors.filter(
          (v: any) =>
            v.victimKey.toString() === victimKey &&
            INJURY_POST.INJURED.includes(injuryPostKey),
        ),
      );
    }

    deceaseVictims.push(
      ...allVictims.filter(
        (v: any) =>
          v.victimKey.toString() === victimKey &&
          INJURY_POST.DECEASED.includes(injuryPostKey),
      ),
    );
  });

  return {
    blesseeMinorVictims,
    blesseeMajorVictims,
    deceaseVictims: groupByVictimName(deceaseVictims),
  };
};
const getDateOfBirthByVictimKey = (
  victimKey: string,
  victimsCorpo: any[],
): Date | undefined => {
  const victim = victimsCorpo.find(
    v => parseInt(v.victimKey) === parseInt(victimKey),
  );

  return victim ? victim.dateOfBirth : undefined;
};
export const isVictimMinor = (
  victimsCorpo: any,
  victimKey: string,
  rcCorporeal: any,
): boolean => {
  const dateOfBirth = getDateOfBirthByVictimKey(victimKey, victimsCorpo);
  const settlementDate = get(
    rcCorporeal,
    'regulationInfo.documentChiffre.documentChiffreCorporel.documentHeader.settlementDate',
  );

  if (isNil(settlementDate) || isNil(dateOfBirth)) {
    return false;
  }

  return (
    Number(
      moment(settlementDate)
        .diff(dateOfBirth, 'years', true)
        .toFixed(2),
    ) < 18
  );
};
